import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { FileText, Upload, Check, Clock, User, ArrowRight } from "lucide-react";
import { ProgressBar } from "../ProgressBar";
import { useSavedTemplates } from "../../hooks";
import { NavigationButtons } from "../shared/NavigationButtons";
import { PageHeader } from "@shared/components/PageHeader";

// Template List Item Component
const TemplateItem = ({ template, isSelected, onSelect }) => {
  return (
    <div
      onClick={() => onSelect(template.id)}
      className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
        isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4
            className={`font-medium ${
              isSelected ? "text-blue-900" : "text-gray-900"
            }`}
          >
            {template.name}
          </h4>
          <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <User size={14} />
              <span>{template.name}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock size={14} />
              <span>{template.createdDate}</span>
            </div>
            <span className="font-medium">{template.usageCount} times</span>
          </div>
        </div>
        {isSelected && (
          <div className="ml-3">
            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
              <Check size={12} className="text-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const UploadMethodSelection = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [selectedMethod, setSelectedMethod] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const hasLogged = useRef(false);

  const { data, isLoading, error, refetch } = useSavedTemplates(1, 50, "");

  // Get data type from previous selection
  const dataType = state?.dataType || "Consumer"; // fallback for demo
  const operationType = state?.operation_type || 2;

  useEffect(() => {
    if (!hasLogged.current && state) {
      hasLogged.current = true;
    }
  }, [state]);

  // Simulate API call to fetch templates
  useEffect(() => {
    if (selectedMethod === "template") {
      setIsLoadingTemplates(true);
      // Simulate API delay
      setTimeout(() => {
        const filteredTemplates = data?.result.filter(
          (template) => template.dataTypeDisplay === dataType
        );
        setAvailableTemplates(filteredTemplates);
        setIsLoadingTemplates(false);
      }, 500); // Reduced delay for better UX
    } else {
      setAvailableTemplates([]); // Clear templates when not using template method
    }
  }, [selectedMethod, dataType]);

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setSelectedTemplate(""); // Reset template selection when method changes
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
  };

  const handleContinue = () => {
    if (selectedMethod === "independent") {
      // Navigate to file upload for independent upload
      navigate("/onboarding/data-migration/file-upload", {
        state: {
          ...state,
          uploadMethod: "independent",
          categoryKey: state?.categoryKey,
        },
      });
    } else if (selectedMethod === "template" && selectedTemplate) {
      // Navigate to file upload with selected template
      const template = availableTemplates.find(
        (t) => t.id === selectedTemplate
      );
      navigate("/onboarding/data-migration/file-upload", {
        state: {
          ...state,
          uploadMethod: "template",
          selectedTemplate: template,
          templateId: template.id,
          categoryKey: state?.categoryKey,
        },
      });
    }
  };

  const canContinue =
    selectedMethod === "independent" ||
    (selectedMethod === "template" && selectedTemplate !== "");

  return (
    <div className="min-h-screen w-full bg-white">
      <ProgressBar
        currentStep={2}
        totalSteps={7}
        progress={28}
        currentStepName={"Upload Method"}
      />

      <main className="w-full mx-auto px-6 py-8">
        <div className="mb-8">
          <PageHeader
            title="Choose Upload Method"
            subtitle={
              <>
                How would you like to upload your{" "}
                <span className="text-blue-600 font-medium">{dataType}</span>{" "}
                data?
              </>
            }
          />

          {/* Upload Method Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 mt-8">
            {/* Use Saved Template Card */}
            <Card
              className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-1 ${
                selectedMethod === "template"
                  ? "border-2 border-blue-500 shadow-md"
                  : "border-2 border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => handleMethodSelect("template")}
            >
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <div
                    className={`inline-flex p-3 rounded-full mb-4 ${
                      selectedMethod === "template"
                        ? "bg-green-200"
                        : "bg-green-100"
                    }`}
                  >
                    <FileText
                      size={24}
                      className={
                        selectedMethod === "template"
                          ? "text-green-700"
                          : "text-green-600"
                      }
                    />
                  </div>
                  <h3
                    className={`text-xl font-semibold mb-2 ${
                      selectedMethod === "template"
                        ? "text-blue-900"
                        : "text-gray-900"
                    }`}
                  >
                    Use Saved Template
                  </h3>
                  <p
                    className={`text-sm leading-relaxed ${
                      selectedMethod === "template"
                        ? "text-blue-700"
                        : "text-gray-600"
                    }`}
                  >
                    Upload using a pre-configured template with field mappings
                    and transformations already set up.
                  </p>
                </div>

                {/* Benefits List */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-sm text-green-600">
                    <Check size={16} />
                    <span>Pre-mapped columns and transformations</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-green-600">
                    <Check size={16} />
                    <span>Faster upload process</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-green-600">
                    <Check size={16} />
                    <span>Consistent data structure</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upload Independently Card */}
            <Card
              className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-1 ${
                selectedMethod === "independent"
                  ? "border-2 border-blue-500 shadow-md"
                  : "border-2 border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => handleMethodSelect("independent")}
            >
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <div
                    className={`inline-flex p-3 rounded-full mb-4 ${
                      selectedMethod === "independent"
                        ? "bg-blue-200"
                        : "bg-blue-100"
                    }`}
                  >
                    <Upload
                      size={24}
                      className={
                        selectedMethod === "independent"
                          ? "text-blue-700"
                          : "text-blue-600"
                      }
                    />
                  </div>
                  <h3
                    className={`text-xl font-semibold mb-2 ${
                      selectedMethod === "independent"
                        ? "text-blue-900"
                        : "text-gray-900"
                    }`}
                  >
                    Upload Independently
                  </h3>
                  <p
                    className={`text-sm leading-relaxed ${
                      selectedMethod === "independent"
                        ? "text-blue-700"
                        : "text-gray-600"
                    }`}
                  >
                    Configure field mappings and transformations from scratch
                    for this specific upload.
                  </p>
                </div>

                {/* Benefits List */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-sm text-blue-600">
                    <Check size={16} />
                    <span>Full control over field mapping</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-blue-600">
                    <Check size={16} />
                    <span>Customize transformations</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-blue-600">
                    <Check size={16} />
                    <span>Option to save as new template</span>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    Best for: New data formats or one-time uploads
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Available Templates Section */}
          {selectedMethod === "template" && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Available Templates
                </h3>
                <span className="text-sm text-gray-600">
                  {isLoadingTemplates
                    ? "Loading..."
                    : `${availableTemplates.length} found`}
                </span>
              </div>

              {isLoadingTemplates ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">
                      Loading available templates...
                    </p>
                  </div>
                </div>
              ) : availableTemplates.length > 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 max-h-64 overflow-y-auto">
                  <div className="p-4 space-y-3">
                    {availableTemplates.map((template) => (
                      <TemplateItem
                        key={template.id}
                        template={template}
                        isSelected={selectedTemplate === template.id}
                        onSelect={handleTemplateSelect}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-8">
                  <div className="text-center">
                    <p className="text-gray-600">
                      No templates available for {dataType} data type.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <NavigationButtons
          onBack={() => navigate(-1)}
          onContinue={handleContinue}
          canContinue={canContinue}
        />
      </main>
    </div>
  );
};

export default UploadMethodSelection;
